import { z } from 'zod';

/**
 * Schemas Zod dos parametros de cada operacao (FR-031).
 * API e Web importam daqui — a UI nao reinventa validacao.
 */

const nonEmpty = (label: string) => z.string().trim().min(1, `${label} é obrigatório`);

export const organizationIdSchema = nonEmpty('organizationId').max(64);
export const isoDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'use o formato AAAA-MM-DD');

export const TARGET_SCHEMAS = [
  'public',
  'datahouse',
  'providers_raw',
  'gold',
  'bronze',
  'silver',
] as const;
export type TargetSchema = (typeof TARGET_SCHEMAS)[number];

export const targetSchemaSchema = z.enum(TARGET_SCHEMAS);

export const RETAILER_SCOPE_MODES = ['include', 'exclude'] as const;
export const RETAILER_PRESET_IDS = [
  'chamex-latam',
  'chamex-br-exclude-latam',
] as const;

export const orgExtractParams = z.object({
  organizationId: organizationIdSchema,
  schemas: z.array(targetSchemaSchema).min(1).optional(),
  includeSnapshots: z.boolean().default(true),
  onConflict: z.enum(['do-nothing', 'do-update']).default('do-nothing'),
  maxRowsPerTable: z.number().int().min(1).max(1_000_000).default(50_000),
  /** include = só estes sites; exclude = org menos estes. */
  retailerMode: z.enum(RETAILER_SCOPE_MODES).optional(),
  /** UUIDs de site_configs (organization_watched_sites.site_id). */
  siteConfigIds: z.array(z.string().trim().min(1)).optional(),
  /** Preset Chamex — preenche siteConfigIds + mode se estes estiverem vazios. */
  retailerPreset: z.enum(RETAILER_PRESET_IDS).optional(),
  gzip: z.boolean().default(true),
});

export const orgExtractByBrandParams = z
  .object({
    organizationId: organizationIdSchema,
    brandId: z.string().trim().min(1).optional(),
    brandName: z.string().trim().min(1).optional(),
    categoryId: z.string().trim().min(1).optional(),
    categoryName: z.string().trim().min(1).optional(),
    onConflict: z.enum(['do-nothing', 'do-update']).default('do-nothing'),
    maxRowsPerTable: z.number().int().min(1).max(1_000_000).default(50_000),
    gzip: z.boolean().default(true),
  })
  .refine((v) => Boolean(v.brandId ?? v.brandName ?? v.categoryId ?? v.categoryName), {
    message: 'informe marca ou categoria',
    path: ['brandName'],
  });

export const orgExtractByBuParams = z.object({
  organizationId: organizationIdSchema,
  businessUnitId: z.string().trim().min(1, 'businessUnitId é obrigatório'),
  schemas: z.array(targetSchemaSchema).min(1).optional(),
  includeSnapshots: z.boolean().default(true),
  onConflict: z.enum(['do-nothing', 'do-update']).default('do-nothing'),
  maxRowsPerTable: z.number().int().min(1).max(1_000_000).default(50_000),
  gzip: z.boolean().default(true),
});

export const orgDeleteParams = z.object({
  organizationId: organizationIdSchema,
  deleteOrphanedUsers: z.boolean().default(false),
});

export const orgInsertParams = z.object({
  organizationId: organizationIdSchema,
  artifactId: z.string().trim().min(1).optional(),
  sql: z.string().min(1).optional(),
  mode: z.enum(['insert-only', 'upsert']).default('insert-only'),
  /** Após o insert, reconstrói gold.industry_product_* a partir de industry_product_site_*. */
  materializeProductRollups: z.boolean().default(false),
  /** Adapta casts jsonb→text[] e omite tabelas irrelevantes (mantém categories). */
  localSchemaAdaptations: z.boolean().default(false),
  /**
   * Com a org de origem viva no mesmo Postgres: novos UUIDs para product_mappings e
   * categories, e restaura product-images/{origem}/ após o rewrite de organization_id.
   */
  coexistenceRemap: z.boolean().default(true),
});

export const GOLD_PRODUCT_WINDOWS = ['d0', 'd7', 'd30', 'd90'] as const;
export type GoldProductWindow = (typeof GOLD_PRODUCT_WINDOWS)[number];

export const goldMaterializeProductRollupsParams = z.object({
  organizationId: organizationIdSchema,
  windows: z.array(z.enum(GOLD_PRODUCT_WINDOWS)).min(1).default([...GOLD_PRODUCT_WINDOWS]),
});

export const EXTRACT_PACKAGE_SPLITS = ['br', 'latam'] as const;

export const orgExtractPackageParams = z.object({
  artifactId: z.string().trim().min(1),
  destOrgId: organizationIdSchema,
  destOrgName: nonEmpty('destOrgName').max(255),
  splitLabel: z.enum(EXTRACT_PACKAGE_SPLITS).default('latam'),
  envLabel: z.string().trim().min(1).max(32).default('STG'),
  gzip: z.boolean().default(true),
  /**
   * Reescreve organization_id no body, remapeia product_mappings/categories e corrige
   * product-images/{destino}/ → product-images/{origem}/ no artefato empacotado.
   */
  coexistenceRemap: z.boolean().default(true),
});

export const orgWipeDataParams = z.object({
  organizationId: organizationIdSchema,
  keepConnections: z.boolean().default(true),
});

export const BRONZE_GOLD_CHECKS = [
  'industry-product-site',
  'industry-site',
  'sov',
  'ratings-gap',
  'quarantine',
  'contract-quarantine',
  'ads',
] as const;
export type BronzeGoldCheck = (typeof BRONZE_GOLD_CHECKS)[number];

export const bronzeGoldValidateParams = z.object({
  organizationId: organizationIdSchema,
  fromDate: isoDateSchema.optional(),
  toDate: isoDateSchema.optional(),
  checks: z.array(z.enum(BRONZE_GOLD_CHECKS)).min(1).optional(),
});

export const snapshotsLatestParams = z.object({
  organizationId: organizationIdSchema,
  schemas: z.array(targetSchemaSchema).min(1).optional(),
});

export const PRODUCT_DELETE_SCOPES = ['brand', 'retailer'] as const;
export type ProductDeleteScope = (typeof PRODUCT_DELETE_SCOPES)[number];

export const productsDeleteByBrandParams = z
  .object({
    organizationId: organizationIdSchema,
    scope: z.enum(PRODUCT_DELETE_SCOPES).default('brand'),
    brandId: z.string().trim().min(1).optional(),
    brandName: z.string().trim().min(1).optional(),
    siteConfigId: z.string().trim().min(1).optional(),
    alsoWipeAnalytics: z.boolean().default(true),
    alsoUnwatchRetailer: z.boolean().default(true),
    /**
     * Scope retailer: hard-delete product_mappings observados neste site (catálogo da org).
     * Default false — apagar varejista não deve remover o catálogo inteiro dos SKUs
     * (nem fatos org-wide em outros sites) sem opt-in explícito.
     */
    alsoDeleteProducts: z.boolean().default(false),
    deleteEmptyBrand: z.boolean().default(false),
  })
  .superRefine((value, ctx) => {
    if (value.scope === 'brand' && !value.brandId && !value.brandName) {
      ctx.addIssue({
        code: 'custom',
        message: 'informe brandId ou brandName',
        path: ['brandName'],
      });
    }
    if (value.scope === 'retailer' && !value.siteConfigId) {
      ctx.addIssue({
        code: 'custom',
        message: 'informe o varejista',
        path: ['siteConfigId'],
      });
    }
  });

/**
 * Superfícies cruzadas fonte → alvo. `keywords` habilita o DELETE de SoV;
 * `sovBrand`/`sovCategory` estreitam esse DELETE; `eans` só apaga com
 * `alsoDeleteProducts`; `catalogBrands`/`catalogCategories` são só leitura.
 */
export const CONTAMINATION_DIMENSIONS = [
  'keywords',
  'sovBrand',
  'sovCategory',
  'eans',
  'catalogBrands',
  'catalogCategories',
] as const;
export type ContaminationDimension = (typeof CONTAMINATION_DIMENSIONS)[number];

export const CONTAMINATION_DEFAULT_DIMENSIONS: readonly ContaminationDimension[] = [
  'keywords',
  'sovBrand',
  'sovCategory',
  'eans',
  'catalogBrands',
  'catalogCategories',
];

export const contaminationCrossOrgParams = z
  .object({
    organizationId: organizationIdSchema,
    sourceOrganizationId: nonEmpty('sourceOrganizationId').max(64),
    dimensions: z
      .array(z.enum(CONTAMINATION_DIMENSIONS))
      .min(1)
      .default([...CONTAMINATION_DEFAULT_DIMENSIONS]),
    alsoDeleteProducts: z.boolean().default(false),
  })
  .superRefine((value, ctx) => {
    if (value.organizationId === value.sourceOrganizationId) {
      ctx.addIssue({
        code: 'custom',
        message: 'a org de referência precisa ser diferente da org a limpar',
        path: ['sourceOrganizationId'],
      });
    }
    if (value.alsoDeleteProducts && !value.dimensions.includes('eans')) {
      ctx.addIssue({
        code: 'custom',
        message: 'apagar produtos exige a dimensão EAN',
        path: ['alsoDeleteProducts'],
      });
    }
  });

export const productsInsertWithSelloutParams = z.object({
  organizationId: organizationIdSchema,
  products: z
    .array(
      z.object({
        ean: z.string().trim().min(1),
        name: z.string().trim().min(1),
        brand: z.string().trim().min(1).optional(),
        category: z.string().trim().min(1).optional(),
      }),
    )
    .min(1, 'informe ao menos 1 produto'),
  sellout: z
    .array(
      z.object({
        ean: z.string().trim().min(1),
        snapshotDate: isoDateSchema,
        varejo: z.string().trim().min(1),
        selloutValor: z.number().optional(),
        selloutUnidades: z.number().optional(),
      }),
    )
    .default([]),
});

export const CONFIG_SECTIONS = [
  'settings',
  'dashboards',
  'watched-sites',
  'ads-targets',
  'business-units',
  'connections-meta',
] as const;
export type ConfigSection = (typeof CONFIG_SECTIONS)[number];

export const configExportParams = z.object({
  organizationId: organizationIdSchema,
  include: z.array(z.enum(CONFIG_SECTIONS)).min(1).optional(),
});

export const configImportParams = z.object({
  organizationId: organizationIdSchema,
  artifactId: z.string().trim().min(1).optional(),
  sql: z.string().min(1).optional(),
  mode: z.enum(['merge', 'replace-selected']).default('merge'),
  include: z.array(z.enum(CONFIG_SECTIONS)).min(1).optional(),
});

export const CATALOG_SECTIONS = [
  'brands',
  'categories',
  'products',
  'links',
  'costs',
  'site-rules',
  'tags',
  'business-units',
] as const;
export type CatalogSection = (typeof CATALOG_SECTIONS)[number];

export const catalogExportParams = z.object({
  organizationId: organizationIdSchema,
  include: z.array(z.enum(CATALOG_SECTIONS)).min(1).optional(),
});

export const catalogImportParams = z.object({
  organizationId: organizationIdSchema,
  artifactId: z.string().trim().min(1).optional(),
  sql: z.string().min(1).optional(),
  mode: z.enum(['merge', 'replace-selected']).default('merge'),
  include: z.array(z.enum(CATALOG_SECTIONS)).min(1).optional(),
});

export const userDeleteParams = z.object({
  userId: nonEmpty('userId').max(64),
  reassignToUserId: z.string().trim().min(1).optional(),
});

export const sqlRunLibraryParams = z
  .object({
    sqlLibraryId: z.string().trim().min(1).optional(),
    sql: z.string().trim().min(1).optional(),
    bindParams: z.array(z.union([z.string(), z.number(), z.boolean(), z.null()])).default([]),
    readOnlyHint: z.boolean().optional(),
    maxRows: z.number().int().min(1).max(100_000).default(1000),
  })
  .refine((v) => Boolean(v.sqlLibraryId ?? v.sql), {
    message: 'informe sqlLibraryId ou cole um SQL',
    path: ['sql'],
  });

export const BACKUP_MODES = ['full', 'schema-only', 'organization'] as const;
export type BackupMode = (typeof BACKUP_MODES)[number];

export const dbBackupParams = z
  .object({
    mode: z.enum(BACKUP_MODES).default('full'),
    organizationId: z.string().trim().min(1).optional(),
    note: z.string().trim().max(500).optional(),
  })
  .refine((v) => v.mode !== 'organization' || Boolean(v.organizationId), {
    message: 'organizationId é obrigatório no modo "por organização"',
    path: ['organizationId'],
  });

export const dbRestoreParams = z.object({
  backupId: nonEmpty('backupId'),
  dropExisting: z.boolean().default(false),
});

export const assetsUploadImagesParams = z.object({
  organizationId: organizationIdSchema,
  artifactId: z.string().trim().min(1, 'artifactId é obrigatório'),
  prefix: z.string().trim().min(1).max(64).default('fds-refs'),
});

export const csvIngestZipParams = z.object({
  organizationId: organizationIdSchema,
  artifactId: z.string().trim().min(1, 'artifactId é obrigatório'),
  skipInvalid: z.boolean().default(false),
  skipMismatchedRetailers: z.boolean().default(false),
  processAfterUpload: z.boolean().default(true),
});

export type OrgExtractParams = z.infer<typeof orgExtractParams>;
export type OrgExtractByBrandParams = z.infer<typeof orgExtractByBrandParams>;
export type OrgExtractByBuParams = z.infer<typeof orgExtractByBuParams>;
export type OrgDeleteParams = z.infer<typeof orgDeleteParams>;
export type OrgInsertParams = z.infer<typeof orgInsertParams>;
export type OrgWipeDataParams = z.infer<typeof orgWipeDataParams>;
export type BronzeGoldValidateParams = z.infer<typeof bronzeGoldValidateParams>;
export type SnapshotsLatestParams = z.infer<typeof snapshotsLatestParams>;
export type ProductsDeleteByBrandParams = z.infer<typeof productsDeleteByBrandParams>;
export type ProductsInsertWithSelloutParams = z.infer<typeof productsInsertWithSelloutParams>;
export type ContaminationCrossOrgParams = z.infer<typeof contaminationCrossOrgParams>;
export type ConfigExportParams = z.infer<typeof configExportParams>;
export type ConfigImportParams = z.infer<typeof configImportParams>;
export type CatalogExportParams = z.infer<typeof catalogExportParams>;
export type CatalogImportParams = z.infer<typeof catalogImportParams>;
export type UserDeleteParams = z.infer<typeof userDeleteParams>;
export type SqlRunLibraryParams = z.infer<typeof sqlRunLibraryParams>;
export type DbBackupParams = z.infer<typeof dbBackupParams>;
export type DbRestoreParams = z.infer<typeof dbRestoreParams>;
export type GoldMaterializeProductRollupsParams = z.infer<typeof goldMaterializeProductRollupsParams>;
export type AssetsUploadImagesParams = z.infer<typeof assetsUploadImagesParams>;
export type CsvIngestZipParams = z.infer<typeof csvIngestZipParams>;
export type OrgExtractPackageParams = z.infer<typeof orgExtractPackageParams>;
